import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ResponsiveLayout } from '@/components/ResponsiveLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  Wallet, Plus, Loader2, ArrowDownLeft, ArrowUpRight, Trash2,
  MoreVertical, Building2, User, TrendingDown, TrendingUp, ChevronDown, ChevronUp
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { SectionHeader } from '@/components/SectionHeader';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const spendCategories = [
  'FB Ads', 'IG Ads', 'TikTok Ads', 'Google Ads', 'YouTube Ads',
  'Email Marketing', 'AI Tools', 'Content Production', 'Events', 'Others'
];

export default function MarketingFundPage() {
  const [funds, setFunds] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [channels, setChannels] = useState([]);
  const [canApprove, setCanApprove] = useState(false);
  const [showAddFund, setShowAddFund] = useState(false);
  const [showAddTx, setShowAddTx] = useState(false);
  const [activeFund, setActiveFund] = useState(null);
  const [expandedFund, setExpandedFund] = useState(null);
  const [channelFilter, setChannelFilter] = useState('all');
  const [newFund, setNewFund] = useState({ channel_id: '', fund_source: 'investor', investor_name: '', initial_amount: '', start_date: '', description: '' });
  const [newTx, setNewTx] = useState({ type: 'spend', amount: '', description: '', category: 'FB Ads', date: new Date().toISOString().slice(0, 10) });

  const token = localStorage.getItem('token');
  const h = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [fundRes, chRes, accessRes] = await Promise.all([
        axios.get(`${API}/finance/marketing-funds`, h),
        axios.get(`${API}/ib/list`, h).catch(() => ({ data: [] })),
        axios.get(`${API}/finance/access-check`, h),
      ]);
      setFunds(fundRes.data.funds || []);
      setSummary(fundRes.data.summary || {});
      setChannels(Array.isArray(chRes.data) ? chRes.data : []);
      setCanApprove(accessRes.data.can_approve);
    } catch (e) {
      if (e.response?.status === 403) toast.error('Akses ditolak');
      else toast.error('Gagal memuatkan data');
    } finally { setLoading(false); }
  };

  const handleCreateFund = async () => {
    if (!newFund.channel_id || !newFund.initial_amount) { toast.error('Sila isi channel dan amount'); return; }
    try {
      const ch = channels.find(c => c.id === newFund.channel_id);
      await axios.post(`${API}/finance/marketing-funds`, { ...newFund, channel_name: ch?.name || newFund.channel_id, initial_amount: parseFloat(newFund.initial_amount) }, h);
      setShowAddFund(false);
      setNewFund({ channel_id: '', fund_source: 'investor', investor_name: '', initial_amount: '', start_date: '', description: '' });
      toast.success('Marketing fund created!');
      fetchData();
    } catch { toast.error('Failed'); }
  };

  const handleAddTransaction = async () => {
    if (!newTx.amount || !activeFund) { toast.error('Sila isi amount'); return; }
    try {
      await axios.post(`${API}/finance/marketing-funds/${activeFund}/transaction`, { ...newTx, amount: parseFloat(newTx.amount) }, h);
      setShowAddTx(false);
      setNewTx({ type: 'spend', amount: '', description: '', category: 'FB Ads', date: new Date().toISOString().slice(0, 10) });
      toast.success('Transaction added!');
      fetchData();
    } catch { toast.error('Failed'); }
  };

  const handleDeleteFund = async (id) => {
    if (!window.confirm('Delete this fund record?')) return;
    try { await axios.delete(`${API}/finance/marketing-funds/${id}`, h); fetchData(); toast.success('Deleted'); } catch { toast.error('Failed'); }
  };

  const handleDeleteTransaction = async (fundId, txId) => {
    if (!window.confirm('Delete this transaction?')) return;
    try {
      await axios.delete(`${API}/finance/marketing-funds/${fundId}/transaction/${txId}`, h);
      fetchData();
      toast.success('Transaction deleted');
    } catch { toast.error('Failed'); }
  };

  const filtered = funds.filter(f => channelFilter === 'all' || f.channel_id === channelFilter);

  if (loading) return <ResponsiveLayout><div className="flex-1 flex items-center justify-center min-h-screen bg-[hsl(var(--background))]"><Loader2 className="w-8 h-8 animate-spin text-[hsl(var(--primary))]" /></div></ResponsiveLayout>;

  return (
    <ResponsiveLayout>
      <div className="flex-1 min-h-screen bg-[hsl(var(--background))] text-[hsl(var(--text-primary))] p-4 lg:p-6">
        <SectionHeader icon={Wallet} title="Marketing Fund" subtitle="Track external & company marketing funds per channel"
          action={<Button onClick={() => setShowAddFund(true)} className="bg-gradient-to-r from-violet-500 to-purple-600 text-white" data-testid="new-fund-btn"><Plus size={18} className="mr-2" /> New Fund</Button>}
        />

        {/* Summary */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
          {[
            { label: 'Total Funds', value: summary.total_funds || 0 },
            { label: 'Total Received', value: `RM ${(summary.total_received || 0).toLocaleString()}`, vc: 'text-emerald-400', accent: 'border-emerald-500/30' },
            { label: 'Total Spent', value: `RM ${(summary.total_spent || 0).toLocaleString()}`, vc: 'text-red-400', accent: 'border-red-500/30' },
            { label: 'Total Balance', value: `RM ${(summary.total_balance || 0).toLocaleString()}`, vc: 'text-blue-400', accent: 'border-blue-500/30' },
          ].map(s => (
            <div key={s.label} className={`bg-[hsl(var(--card))] border ${s.accent || 'border-[hsl(var(--border))]'} rounded-xl p-4`}>
              <p className={`text-xs ${s.vc || 'text-[hsl(var(--text-muted))]'} mb-1`}>{s.label}</p>
              <p className={`text-xl font-bold ${s.vc || ''}`}>{s.value}</p>
            </div>
          ))}
        </div>

        {/* Channel Filter */}
        <div className="flex gap-3 mb-6">
          <Select value={channelFilter} onValueChange={setChannelFilter}>
            <SelectTrigger className="w-44 bg-[hsl(var(--surface))] border-[hsl(var(--border))]"><SelectValue placeholder="All Channels" /></SelectTrigger>
            <SelectContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
              <SelectItem value="all">All Channels</SelectItem>
              {channels.map(ch => <SelectItem key={ch.id} value={ch.id}>{ch.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {/* Fund Cards */}
        <div className="space-y-4">
          {filtered.length === 0 ? (
            <div className="text-center py-16 bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-xl">
              <Wallet size={48} className="mx-auto text-violet-500/20 mb-4" />
              <p className="text-[hsl(var(--text-secondary))]">No marketing funds recorded</p>
            </div>
          ) : filtered.map(fund => {
            const isExpanded = expandedFund === fund.id;
            const usagePercent = fund.total_received > 0 ? Math.round((fund.total_spent / fund.total_received) * 100) : 0;
            return (
              <div key={fund.id} className="bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-xl overflow-hidden" data-testid={`fund-${fund.id}`}>
                {/* Fund Header */}
                <div className="p-5">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${fund.fund_source === 'company' ? 'bg-blue-500/20' : fund.fund_source === 'brokers' ? 'bg-amber-500/20' : 'bg-violet-500/20'}`}>
                        {fund.fund_source === 'company' ? <Building2 size={20} className="text-blue-400" /> : fund.fund_source === 'brokers' ? <TrendingUp size={20} className="text-amber-400" /> : <User size={20} className="text-violet-400" />}
                      </div>
                      <div>
                        <h3 className="font-semibold">{fund.channel_name}</h3>
                        <p className="text-xs text-[hsl(var(--text-muted))]">
                          {fund.fund_source === 'company' ? 'Company Fund (Underads)' : fund.fund_source === 'brokers' ? `Broker: ${fund.investor_name}` : `Investor: ${fund.investor_name}`}
                        </p>
                        {fund.description && <p className="text-xs text-[hsl(var(--text-secondary))] mt-0.5">{fund.description}</p>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button size="sm" variant="outline" className="text-xs border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10"
                        onClick={() => { setActiveFund(fund.id); setNewTx({ ...newTx, type: 'spend' }); setShowAddTx(true); }}>
                        <ArrowUpRight size={12} className="mr-1" /> Add Spend
                      </Button>
                      <Button size="sm" variant="outline" className="text-xs border-blue-500/30 text-blue-400 hover:bg-blue-500/10"
                        onClick={() => { setActiveFund(fund.id); setNewTx({ ...newTx, type: 'received' }); setShowAddTx(true); }}>
                        <ArrowDownLeft size={12} className="mr-1" /> Add Fund
                      </Button>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" className="h-8 w-8"><MoreVertical size={14} /></Button></DropdownMenuTrigger>
                        <DropdownMenuContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
                          {canApprove && <DropdownMenuItem onClick={() => handleDeleteFund(fund.id)} className="text-red-400"><Trash2 size={14} className="mr-2" /> Delete</DropdownMenuItem>}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>

                  {/* Balance Bar */}
                  <div className="grid grid-cols-3 gap-4 mb-3">
                    <div><p className="text-[10px] text-[hsl(var(--text-muted))] uppercase">Received</p><p className="text-lg font-bold text-emerald-400">RM {fund.total_received?.toLocaleString()}</p></div>
                    <div><p className="text-[10px] text-[hsl(var(--text-muted))] uppercase">Spent</p><p className="text-lg font-bold text-red-400">RM {fund.total_spent?.toLocaleString()}</p></div>
                    <div><p className="text-[10px] text-[hsl(var(--text-muted))] uppercase">Balance</p><p className={`text-lg font-bold ${fund.balance >= 0 ? 'text-blue-400' : 'text-red-400'}`}>RM {fund.balance?.toLocaleString()}</p></div>
                  </div>
                  <div className="w-full bg-[hsl(var(--border))] rounded-full h-2">
                    <div className={`h-2 rounded-full transition-all ${usagePercent > 80 ? 'bg-red-500' : usagePercent > 50 ? 'bg-amber-500' : 'bg-emerald-500'}`} style={{ width: `${Math.min(usagePercent, 100)}%` }} />
                  </div>
                  <p className="text-[10px] text-[hsl(var(--text-muted))] mt-1">{usagePercent}% used · Created by {fund.created_by_name}</p>

                  {/* Toggle Transactions */}
                  <button onClick={() => setExpandedFund(isExpanded ? null : fund.id)} className="flex items-center gap-1 text-xs text-violet-400 mt-3 hover:underline">
                    {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                    {isExpanded ? 'Hide' : 'Show'} Transactions ({(fund.transactions || []).length})
                  </button>
                </div>

                {/* Transaction Table */}
                {isExpanded && (
                  <div className="border-t border-[hsl(var(--border))]">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-[hsl(var(--surface))]">
                          <th className="text-left py-2 px-4 text-xs text-[hsl(var(--text-muted))] font-medium">Date</th>
                          <th className="text-left py-2 px-4 text-xs text-[hsl(var(--text-muted))] font-medium">Description</th>
                          <th className="text-right py-2 px-4 text-xs text-[hsl(var(--text-muted))] font-medium">Received</th>
                          <th className="text-right py-2 px-4 text-xs text-[hsl(var(--text-muted))] font-medium">Spend</th>
                          <th className="text-right py-2 px-4 text-xs text-[hsl(var(--text-muted))] font-medium">Balance</th>
                          <th className="text-right py-2 px-4 text-xs text-[hsl(var(--text-muted))] font-medium">By</th>
                          <th className="text-right py-2 px-2 text-xs text-[hsl(var(--text-muted))] font-medium"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {(fund.transactions || []).map((tx, i) => (
                          <tr key={tx.id || i} className="border-t border-[hsl(var(--border))]/50 hover:bg-white/5">
                            <td className="py-2 px-4 text-xs">{tx.date}</td>
                            <td className="py-2 px-4 text-xs">{tx.description}{tx.category ? ` (${tx.category})` : ''}</td>
                            <td className="py-2 px-4 text-xs text-right text-emerald-400">{tx.type === 'received' ? `RM ${tx.amount?.toLocaleString()}` : ''}</td>
                            <td className="py-2 px-4 text-xs text-right text-red-400">{tx.type === 'spend' ? `RM ${tx.amount?.toLocaleString()}` : ''}</td>
                            <td className="py-2 px-4 text-xs text-right font-medium">{tx.balance !== undefined ? `RM ${tx.balance?.toLocaleString()}` : ''}</td>
                            <td className="py-2 px-4 text-xs text-right text-[hsl(var(--text-muted))]">{tx.created_by}</td>
                            <td className="py-2 px-2 text-right">
                              <button onClick={() => handleDeleteTransaction(fund.id, tx.id)} className="text-red-400/50 hover:text-red-400 transition-colors"><Trash2 size={12} /></button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* New Fund Modal */}
        <Dialog open={showAddFund} onOpenChange={setShowAddFund}>
          <DialogContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))] max-w-md">
            <DialogHeader><DialogTitle className="flex items-center gap-2"><Wallet size={18} className="text-violet-400" /> New Marketing Fund</DialogTitle></DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <label className="text-sm text-[hsl(var(--text-secondary))] mb-1.5 block font-medium">Channel *</label>
                <Select value={newFund.channel_id} onValueChange={(v) => setNewFund({ ...newFund, channel_id: v })}>
                  <SelectTrigger className="bg-[hsl(var(--surface))] border-[hsl(var(--border))]"><SelectValue placeholder="Select channel" /></SelectTrigger>
                  <SelectContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
                    {channels.map(ch => <SelectItem key={ch.id} value={ch.id}>{ch.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm text-[hsl(var(--text-secondary))] mb-1.5 block font-medium">Fund Source *</label>
                <Select value={newFund.fund_source} onValueChange={(v) => setNewFund({ ...newFund, fund_source: v })}>
                  <SelectTrigger className="bg-[hsl(var(--surface))] border-[hsl(var(--border))]"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
                    <SelectItem value="investor">Investor (External)</SelectItem>
                    <SelectItem value="company">Company (Underads)</SelectItem>
                    <SelectItem value="brokers">Brokers</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {(newFund.fund_source === 'investor' || newFund.fund_source === 'brokers') && (
                <div>
                  <label className="text-sm text-[hsl(var(--text-secondary))] mb-1.5 block font-medium">{newFund.fund_source === 'brokers' ? 'Broker Name' : 'Investor Name'}</label>
                  <Input value={newFund.investor_name} onChange={(e) => setNewFund({ ...newFund, investor_name: e.target.value })} placeholder={newFund.fund_source === 'brokers' ? 'Nama broker...' : 'Nama investor...'} className="bg-[hsl(var(--surface))] border-[hsl(var(--border))]" />
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm text-[hsl(var(--text-secondary))] mb-1.5 block font-medium">Initial Amount (RM) *</label>
                  <Input type="number" value={newFund.initial_amount} onChange={(e) => setNewFund({ ...newFund, initial_amount: e.target.value })} placeholder="0.00" className="bg-[hsl(var(--surface))] border-[hsl(var(--border))]" />
                </div>
                <div>
                  <label className="text-sm text-[hsl(var(--text-secondary))] mb-1.5 block font-medium">Start Date</label>
                  <Input type="date" value={newFund.start_date} onChange={(e) => setNewFund({ ...newFund, start_date: e.target.value })} className="bg-[hsl(var(--surface))] border-[hsl(var(--border))] [color-scheme:dark]" />
                </div>
              </div>
              <div>
                <label className="text-sm text-[hsl(var(--text-secondary))] mb-1.5 block font-medium">Description / Purpose</label>
                <textarea value={newFund.description} onChange={(e) => setNewFund({ ...newFund, description: e.target.value })} placeholder="e.g. Fund untuk FB Ads campaign Q1 2026..." rows={2} className="w-full bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-md p-3 text-sm resize-none focus:outline-none" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAddFund(false)}>Cancel</Button>
              <Button onClick={handleCreateFund} className="bg-gradient-to-r from-violet-500 to-purple-600 text-white">Create Fund</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Add Transaction Modal */}
        <Dialog open={showAddTx} onOpenChange={setShowAddTx}>
          <DialogContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))] max-w-md">
            <DialogHeader><DialogTitle className="flex items-center gap-2">
              {newTx.type === 'spend' ? <TrendingDown size={18} className="text-red-400" /> : <TrendingUp size={18} className="text-emerald-400" />}
              {newTx.type === 'spend' ? 'Record Spending' : 'Add Fund Received'}
            </DialogTitle></DialogHeader>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm text-[hsl(var(--text-secondary))] mb-1.5 block font-medium">Amount (RM) *</label>
                  <Input type="number" value={newTx.amount} onChange={(e) => setNewTx({ ...newTx, amount: e.target.value })} placeholder="0.00" className="bg-[hsl(var(--surface))] border-[hsl(var(--border))]" />
                </div>
                <div>
                  <label className="text-sm text-[hsl(var(--text-secondary))] mb-1.5 block font-medium">Date</label>
                  <Input type="date" value={newTx.date} onChange={(e) => setNewTx({ ...newTx, date: e.target.value })} className="bg-[hsl(var(--surface))] border-[hsl(var(--border))] [color-scheme:dark]" />
                </div>
              </div>
              {newTx.type === 'spend' && (
                <div>
                  <label className="text-sm text-[hsl(var(--text-secondary))] mb-1.5 block font-medium">Category</label>
                  <Select value={newTx.category} onValueChange={(v) => setNewTx({ ...newTx, category: v })}>
                    <SelectTrigger className="bg-[hsl(var(--surface))] border-[hsl(var(--border))]"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
                      {spendCategories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <label className="text-sm text-[hsl(var(--text-secondary))] mb-1.5 block font-medium">Description</label>
                <Input value={newTx.description} onChange={(e) => setNewTx({ ...newTx, description: e.target.value })} placeholder="e.g. FB Ads Jan Week 1..." className="bg-[hsl(var(--surface))] border-[hsl(var(--border))]" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAddTx(false)}>Cancel</Button>
              <Button onClick={handleAddTransaction} className={newTx.type === 'spend' ? 'bg-red-500 text-white' : 'bg-emerald-500 text-white'}>
                {newTx.type === 'spend' ? 'Record Spend' : 'Add Fund'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </ResponsiveLayout>
  );
}
